import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, LogOut, Mail, MessageCircle, Filter, RefreshCw,
  Plus, Edit3, Trash2, Eye, EyeOff, X, Download,
} from "lucide-react";

const TYPES = ["all", "eligibility", "consultation", "premium", "newsletter"];

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadLeadsCsv(leads) {
  const rows = [
    ["id", "type", "created_at", "full_name", "email", "phone", "country", "study_level", "track", "message"],
    ...leads.map((l) => [
      l.id, l.lead_type, l.created_at,
      l.data?.full_name, l.data?.email, l.data?.phone, l.data?.country,
      l.data?.study_level, l.data?.track, l.data?.message,
    ]),
  ];
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `studypoland-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- Login ----------
function Login({ onAuth }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/admin/verify", {}, { headers: { "X-Admin-Token": input } });
      localStorage.setItem("sp_admin_token", input);
      onAuth(input);
      toast.success("Signed in");
    } catch {
      toast.error("Invalid admin token");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-40 pb-20 min-h-screen" data-testid="admin-login-page">
      <div className="container-sp max-w-md">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl text-[#0D1B2A] mt-3">Sign in</h1>
        <p className="text-[#4A5568] mt-3">Enter the admin token to view leads and manage insights.</p>
        <form onSubmit={submit} className="card-sp mt-8" data-testid="admin-login-form">
          <label className="label-sp">Admin token</label>
          <input
            type="password"
            className="input-sp"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="admin-token-input"
            required
          />
          <button disabled={loading} className="btn-primary mt-5 w-full justify-center" data-testid="admin-login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------- Leads view ----------
function LeadsView({ headers }) {
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {}, posts: 0 });
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { lead_type: filter };
      const [leadsRes, statsRes] = await Promise.all([
        http.get("/admin/leads", { headers, params }),
        http.get("/admin/stats", { headers }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || { total: 0, by_type: {}, posts: 0 });
    } catch {
      toast.error("Unable to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="admin-leads-view">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
        <div className="card-sp py-5">
          <div className="overline">Total</div>
          <div className="font-serif text-3xl text-[#0D1B2A] mt-2">{stats.total}</div>
        </div>
        {Object.entries(stats.by_type || {}).map(([k, v]) => (
          <div key={k} className="card-sp py-5">
            <div className="overline">{k}</div>
            <div className="font-serif text-3xl text-[#0D1B2A] mt-2">{v}</div>
          </div>
        ))}
        <div className="card-sp py-5">
          <div className="overline">Posts</div>
          <div className="font-serif text-3xl text-[#0D1B2A] mt-2">{stats.posts || 0}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-[#4A5568]" />
        {TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`admin-filter-${t}`}
            className={`px-4 py-2 rounded-full text-xs transition-all ${
              filter === t
                ? "bg-[#0D1B2A] text-white"
                : "bg-white text-[#0D1B2A] border border-[#EFEEE8] hover:border-[#D7141A]/40"
            }`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={refresh} className="btn-outline" data-testid="admin-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => downloadLeadsCsv(leads)}
            className="btn-gold"
            disabled={!leads.length}
            data-testid="admin-export-csv"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="card-sp p-0 overflow-hidden" data-testid="admin-leads-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F1EA] text-[#4A5568] text-left">
              <tr>
                <th className="px-5 py-4 font-medium">Type</th>
                <th className="px-5 py-4 font-medium">Name</th>
                <th className="px-5 py-4 font-medium">Email</th>
                <th className="px-5 py-4 font-medium">Details</th>
                <th className="px-5 py-4 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="5" className="px-5 py-8 text-center text-[#718096]">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan="5" className="px-5 py-8 text-center text-[#718096]">No leads yet.</td></tr>
              )}
              {!loading && leads.map((l) => (
                <tr key={l.id} className="border-t border-[#F0EEE6] hover:bg-[#F8F7F4]" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-5 py-4">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D7141A]">{l.lead_type}</span>
                  </td>
                  <td className="px-5 py-4 text-[#0D1B2A] font-medium">{l.data?.full_name || l.data?.email || "—"}</td>
                  <td className="px-5 py-4 text-[#4A5568]">
                    <a href={`mailto:${l.data?.email}`} className="inline-flex items-center gap-1.5 hover:text-[#D7141A]">
                      <Mail className="w-3.5 h-3.5" />{l.data?.email}
                    </a>
                  </td>
                  <td className="px-5 py-4 text-[#4A5568] max-w-sm">
                    <div className="text-xs space-y-0.5">
                      {l.data?.study_level && <div>Level: {l.data.study_level}</div>}
                      {l.data?.track && <div>Track: {l.data.track}</div>}
                      {l.data?.country && <div>Country: {l.data.country}</div>}
                      {l.data?.phone && (
                        <div className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{l.data.phone}</div>
                      )}
                      {l.data?.message && <div className="italic text-[#718096] mt-1 line-clamp-2">"{l.data.message}"</div>}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-[#718096] text-xs whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- Insights editor ----------
const EMPTY_POST = { title: "", excerpt: "", content: "", category: "Guides", cover_image: "", published: true };

function PostEditor({ headers, editing, onSaved, onClose }) {
  const [form, setForm] = useState(editing || EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) return toast.error("Title and content required");
    setSaving(true);
    try {
      if (editing?.id) {
        await http.patch(`/admin/posts/${editing.id}`, form, { headers });
        toast.success("Post updated");
      } else {
        await http.post("/admin/posts", form, { headers });
        toast.success("Post published");
      }
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-10" data-testid="admin-post-editor">
      <div className="bg-white rounded-2xl w-full max-w-3xl mx-4 p-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-[#EBE9E1]" data-testid="admin-post-editor-close">
          <X className="w-5 h-5" />
        </button>
        <div className="overline">{editing ? "Edit article" : "New article"}</div>
        <h2 className="font-serif text-3xl text-[#0D1B2A] mt-2 mb-6">
          {editing ? "Edit post" : "Write a new post"}
        </h2>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="label-sp">Title *</label>
            <input className="input-sp" value={form.title} onChange={change("title")} required data-testid="admin-post-title" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-sp">Category</label>
              <input className="input-sp" value={form.category} onChange={change("category")} data-testid="admin-post-category" />
            </div>
            <div>
              <label className="label-sp">Cover image URL</label>
              <input className="input-sp" value={form.cover_image} onChange={change("cover_image")} data-testid="admin-post-cover" />
            </div>
          </div>
          <div>
            <label className="label-sp">Excerpt</label>
            <textarea className="input-sp min-h-[80px]" value={form.excerpt} onChange={change("excerpt")} data-testid="admin-post-excerpt" />
          </div>
          <div>
            <label className="label-sp">Content *</label>
            <textarea className="input-sp min-h-[280px] font-mono text-sm" value={form.content} onChange={change("content")} required data-testid="admin-post-content" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0D1B2A]">
            <input type="checkbox" checked={!!form.published} onChange={change("published")} data-testid="admin-post-published" />
            Published
          </label>
          <div className="flex gap-3 pt-3">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-post-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editing ? "Save changes" : "Publish"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InsightsView({ headers }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/posts", { headers });
      setPosts(r.data || []);
    } catch {
      toast.error("Unable to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/admin/posts/${id}`, { headers });
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-insights-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">Insights</div>
          <h2 className="font-serif text-2xl text-[#0D1B2A] mt-1">Articles</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} className="btn-outline" data-testid="admin-posts-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="btn-primary"
            data-testid="admin-new-post"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      <div className="card-sp p-0 overflow-hidden" data-testid="admin-posts-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F1EA] text-[#4A5568] text-left">
            <tr>
              <th className="px-5 py-4 font-medium">Title</th>
              <th className="px-5 py-4 font-medium">Category</th>
              <th className="px-5 py-4 font-medium">Status</th>
              <th className="px-5 py-4 font-medium">Updated</th>
              <th className="px-5 py-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="px-5 py-8 text-center text-[#718096]">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && posts.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-8 text-center text-[#718096]">No posts yet. Create your first article.</td></tr>
            )}
            {!loading && posts.map((p) => (
              <tr key={p.id} className="border-t border-[#F0EEE6]" data-testid={`admin-post-row-${p.slug}`}>
                <td className="px-5 py-4 text-[#0D1B2A] font-medium">{p.title}</td>
                <td className="px-5 py-4 text-[#4A5568] text-xs">{p.category}</td>
                <td className="px-5 py-4">
                  {p.published ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[#D7141A]"><Eye className="w-3 h-3" /> Published</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-[#718096]"><EyeOff className="w-3 h-3" /> Draft</span>
                  )}
                </td>
                <td className="px-5 py-4 text-[#718096] text-xs whitespace-nowrap">
                  {new Date(p.updated_at).toLocaleString()}
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => { setEditing(p); setEditorOpen(true); }} className="p-2 rounded-full hover:bg-[#EBE9E1]" title="Edit" data-testid={`admin-edit-${p.slug}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-[#EBE9E1]" title="Delete" data-testid={`admin-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4 text-[#C53030]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <PostEditor
          headers={headers}
          editing={editing}
          onSaved={() => { setEditorOpen(false); refresh(); }}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </div>
  );
}

// ---------- Root ----------
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("sp_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("leads");

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  useEffect(() => {
    if (!token) return;
    http
      .post("/admin/verify", {}, { headers: { "X-Admin-Token": token } })
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem("sp_admin_token");
    setToken("");
    setAuthed(false);
  };

  if (!authed) return <Login onAuth={(t) => { setToken(t); setAuthed(true); }} />;

  return (
    <div className="pt-32 pb-20 min-h-screen" data-testid="admin-dashboard">
      <div className="container-sp">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="overline">Admin</div>
            <h1 className="font-serif text-4xl text-[#0D1B2A] mt-2">Dashboard</h1>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout">
            <LogOut className="w-4 h-4 inline mr-1.5" /> Sign out
          </button>
        </div>

        <div className="flex gap-1 mb-10 border-b border-[#E5E3D8]">
          {[{ k: "leads", l: "Leads" }, { k: "insights", l: "Insights" }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.k
                  ? "border-[#D7141A] text-[#0D1B2A]"
                  : "border-transparent text-[#718096] hover:text-[#0D1B2A]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsView headers={headers} />}
        {tab === "insights" && <InsightsView headers={headers} />}
      </div>
    </div>
  );
}
